/**
 * Filing by hand: the decisions, with no browser in them.
 *
 * The sidebar files an exploration by dragging its row into a group, and by a
 * keyboard grab for everybody a drag strands. Both are choreography — pointers,
 * timers, measured boxes, focus — and choreography is exactly what a test cannot
 * hold still. So the DECISIONS live here and nothing else does: which group a
 * point is inside, whether a press has become a drag, where a grab goes next,
 * and what to say about it. The component moves the pixels; this says what the
 * pixels mean.
 *
 * REORDERING IS NOT A THING THIS DOES, and the ghost must not be read as saying
 * otherwise. A drop changes MEMBERSHIP only: the groups stay in the order the
 * pane derives (most recently saved first, unfiled last) and the rows inside one
 * stay newest first, whatever height the ghost was let go at. An exploration is
 * not a list item somebody ranked — its position is a fact about when it was
 * last worked on, and a hand-sorted list would be the first thing in this pane
 * that could be wrong.
 *
 * Imports nothing. `null` is the unfiled bucket everywhere below, exactly as it
 * is in `groupRecent`, so a target and a group key are the same value.
 */

/**
 * One group's rectangle on screen, in viewport coordinates.
 *
 * A RECTANGLE and not a band, which it was for one round. Asking only about `y`
 * meant a pointer level with a group but a hundred pixels out on the field was
 * still inside it, so letting go anywhere across the window filed the row — and
 * the guide's promise that "dropping somewhere that is not a group changes
 * nothing" was simply false. The horizontal edges are the list's own, not each
 * group's: the column is the thing being aimed at, and a drop four pixels into
 * the pane's padding is not a miss.
 */
export interface DropBox {
  project: string | null
  left: number
  right: number
  top: number
  bottom: number
}

/**
 * How far a pointer must travel before a press stops being a press.
 *
 * Six pixels, which is the usual figure and is chosen against the FAILURE rather
 * than for the feel: a row's press OPENS an exploration, so a threshold too low
 * turns a slightly shaky click into a filing, and one too high makes a
 * deliberate short drag feel dead. Six is below what a finger or a mouse wobbles
 * on a click and well under any movement somebody means.
 */
export const DRAG_THRESHOLD = 6

/**
 * How long a finger must rest before the row lifts.
 *
 * There is no hover on a touch screen and no threshold that can tell a drag from
 * a scroll at its first pixel — both start as a finger moving up the list — so
 * on a coarse pointer the row is lifted by TIME instead, and any movement before
 * the timer fires is a scroll and cancels it. 350ms is the long-press figure
 * both mobile platforms use for exactly this act.
 */
export const LONG_PRESS_MS = 350

/** How far a finger may drift before the long press is read as a scroll. Larger
 *  than `DRAG_THRESHOLD` because a finger resting is never still. */
export const LONG_PRESS_SLOP = 10

/** How close to an edge of the list the pointer has to be for it to scroll, and
 *  the fastest it will go, in pixels per frame. */
export const AUTOSCROLL_EDGE = 48
export const AUTOSCROLL_MAX = 16

/** Whether a press has travelled far enough to be a drag. */
export function startedDrag(dx: number, dy: number): boolean {
  return Math.hypot(dx, dy) >= DRAG_THRESHOLD
}

/**
 * The group under a point, or null for none — which is a CANCEL and not the
 * unfiled bucket. The unfiled bucket is a box like any other and answers with
 * `{ project: null }`, which is why this returns the box rather than the label.
 *
 * First match wins. Boxes are the group elements as laid out, so they do not
 * overlap; asking in order means a caller that hands over a stale measurement
 * gets a stale answer rather than an arbitrary one.
 *
 * BOTH AXES. See `DropBox`: a caller that only asked about `y` filed a row for a
 * release anywhere across the window at the right height.
 */
export function targetAt(x: number, y: number, boxes: readonly DropBox[]): DropBox | null {
  return boxes.find((b) => x >= b.left && x < b.right && y >= b.top && y < b.bottom) ?? null
}

/**
 * The boxes as they can actually be SEEN, clipped to the list's own rectangle.
 *
 * A group scrolled out of the list is still a rectangle — it is simply above or
 * below the visible column — and an unclipped one lies underneath the tool
 * rows and everything else the pane draws above its list. A drop
 * there filed into whichever group happened to be parked behind the chrome,
 * which is the worst kind of wrong answer: plausible, silent, and not where
 * anybody was pointing.
 *
 * A box with nothing left of it after the clip is dropped rather than kept as an
 * empty sliver, so `targetAt` cannot match a group that is not on screen at all.
 */
export function visibleBoxes(boxes: readonly DropBox[], list: DropBox): DropBox[] {
  const out: DropBox[] = []
  for (const b of boxes) {
    const top = Math.max(b.top, list.top)
    const bottom = Math.min(b.bottom, list.bottom)
    if (bottom <= top) continue
    out.push({ project: b.project, left: list.left, right: list.right, top, bottom })
  }
  return out
}

/**
 * How far to scroll the list this frame, given where the pointer is against the
 * list's own box. Negative is up. Zero everywhere but the two edge bands.
 *
 * Proportional rather than constant: a drag that has to reach a group off the
 * bottom of a long list wants speed, and one hovering just inside the band wants
 * to be able to stop. The ramp is what makes both possible with one number.
 */
export function autoScrollStep(y: number, top: number, bottom: number): number {
  if (y < top + AUTOSCROLL_EDGE) {
    const into = Math.max(0, Math.min(AUTOSCROLL_EDGE, top + AUTOSCROLL_EDGE - y))
    return -Math.round((into / AUTOSCROLL_EDGE) * AUTOSCROLL_MAX)
  }
  if (y > bottom - AUTOSCROLL_EDGE) {
    const into = Math.max(0, Math.min(AUTOSCROLL_EDGE, y - (bottom - AUTOSCROLL_EDGE)))
    return Math.round((into / AUTOSCROLL_EDGE) * AUTOSCROLL_MAX)
  }
  return 0
}

/**
 * A row lifted by the keyboard, and where its target currently stands.
 *
 * The accessible half of the same act, and not an afterthought: filing by drag
 * alone would put a whole feature behind a gesture a keyboard cannot make and a
 * screen reader cannot see. This is the standard grab — lift, cycle, drop — and
 * `at` is an INDEX into the candidate list rather than a label, because that is
 * what makes cycling past the ends well defined.
 */
export interface Grab {
  id: string
  seed: string
  /** The project the row was in when it was lifted, so a cancel can say so. */
  from: string | null
  at: number
}

/** Where a lift starts: on the group the row is already in, so the first ↑ or ↓
 *  moves one place from where the reader is, not from the top of a list they did
 *  not choose. A row whose project is not among the candidates starts at 0. */
export function liftAt(candidates: readonly (string | null)[], from: string | null): number {
  const i = candidates.indexOf(from)
  return i < 0 ? 0 : i
}

/** One step through the candidates, wrapping at both ends. Wrapping rather than
 *  stopping because the list is a ring of choices and not a scale: somebody one
 *  past the end wants the other end, not a dead key. */
export function stepAt(at: number, by: number, n: number): number {
  if (n <= 0) return 0
  return (((at + by) % n) + n) % n
}

/** The candidate a grab is currently pointed at. */
export function grabTarget(grab: Grab, candidates: readonly (string | null)[]): string | null {
  return candidates[grab.at] ?? null
}

/** What a project is called when it is being spoken rather than drawn. The
 *  unfiled bucket's heading says "Recent" and so does this, so a reader hears
 *  the group they would see. RECENT rather than "No project" because the
 *  heading names a place — the reference's word for its ungrouped list, and
 *  the bucket is newest first — where "No project" named an absence, which is
 *  a fact about the row rather than about where it sits (D89, follow-up). */
export function projectSaid(project: string | null): string {
  return project ?? 'Recent'
}

/** The whole sentence a lift announces: what is moving, where it would go, and
 *  the two keys that end it. Said once on lift, because a reader who arrives
 *  mid-gesture with no way out is the failure this pattern exists to avoid. */
export function liftSaid(seed: string, target: string | null): string {
  return `Moving “${seed}”. Target: ${projectSaid(target)}. Enter to drop, Escape to cancel.`
}

/** And what each step announces. The target alone: the rest of the sentence has
 *  not changed, and repeating it turns cycling through six groups into six
 *  paragraphs. */
export function targetSaid(target: string | null): string {
  return `Target: ${projectSaid(target)}.`
}
